import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Info, Loader2, Save } from 'lucide-react';
import { useEffect } from 'react';
import {
  useForm,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PartTypePicker } from '@/components/ui/part-type-picker';
import { Skeleton } from '@/components/ui/skeleton';
import { usePartType } from '@/features/part-types/queries';
import {
  useCreatePart,
  usePart,
  useUpdatePart,
} from '@/features/parts/queries';
import {
  partCreateSchema,
  partUpdateSchema,
  type PartCreateValues,
  type PartUpdateValues,
} from '@/features/parts/schema';
import { isGlobalPart, type Part } from '@/features/parts/types';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

/** Generic TextField — works for create + update shapes. */
function TextField<TForm extends FieldValues>({
  control,
  name,
  label,
  hint,
  type = 'text',
  textarea = false,
}: {
  control: Control<TForm>;
  name: FieldPath<TForm>;
  label: string;
  hint?: string;
  type?: 'text' | 'number';
  textarea?: boolean;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            {textarea ? (
              <textarea
                {...field}
                rows={3}
                value={
                  field.value === null || field.value === undefined
                    ? ''
                    : String(field.value)
                }
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            ) : (
              <Input
                type={type}
                {...field}
                value={
                  field.value === null || field.value === undefined
                    ? ''
                    : String(field.value)
                }
              />
            )}
          </FormControl>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function CheckboxField<TForm extends FieldValues>({
  control,
  name,
  label,
}: {
  control: Control<TForm>;
  name: FieldPath<TForm>;
  label: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-col gap-1">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={Boolean(field.value)}
              onChange={(e) => field.onChange(e.target.checked)}
            />
            {label}
          </label>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function ReadOnlyField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-sm font-medium">{label}</p>
      <p className="mt-1 rounded-md border bg-muted/40 px-3 py-2 text-sm">
        {value}
      </p>
    </div>
  );
}

function CreateBranch({
  defaultPartTypeId,
}: {
  defaultPartTypeId: string | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMut = useCreatePart();

  const form = useForm<PartCreateValues>({
    resolver: zodResolver(partCreateSchema),
    defaultValues: {
      code: '',
      name: '',
      partTypeId: defaultPartTypeId ?? '',
      manufacturer: null,
      manufacturerPartNumber: null,
      unit: null,
      minStockGlobal: null,
      maxStockGlobal: null,
      ownerCompanyId: null,
      notes: null,
    },
  });

  const errorMessage = (() => {
    if (!createMut.error) return null;
    if (createMut.error instanceof ApiError) {
      return t(createMut.error.key, {
        defaultValue: t('part.form.saveError'),
        ...(createMut.error.params ?? {}),
      });
    }
    return t('part.form.saveError');
  })();

  const onSubmit = form.handleSubmit((values) => {
    createMut.mutate(values, {
      onSuccess: (created) => navigate(`/parts/${created.id}`),
    });
  });

  return (
    <FormBody
      mode="create"
      form={form}
      title={t('part.form.createTitle')}
      onSubmit={onSubmit}
      submitting={createMut.isPending}
      submitLabel={t('part.form.submitCreate')}
      cancelTo="/parts"
      errorMessage={errorMessage}
    />
  );
}

function EditBranch({ existing }: { existing: Part }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateMut = useUpdatePart(existing.id);
  const { data: partType } = usePartType(existing.partTypeId);

  const initial: PartUpdateValues = {
    name: existing.name,
    manufacturer: existing.manufacturer,
    manufacturerPartNumber: existing.manufacturerPartNumber,
    unit: existing.unit,
    minStockGlobal: existing.minStockGlobal,
    maxStockGlobal: existing.maxStockGlobal,
    notes: existing.notes,
    isActive: existing.isActive,
  };

  const form = useForm<PartUpdateValues>({
    resolver: zodResolver(partUpdateSchema),
    defaultValues: initial,
  });

  useEffect(() => {
    form.reset(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.id]);

  const errorMessage = (() => {
    if (!updateMut.error) return null;
    if (updateMut.error instanceof ApiError) {
      return t(updateMut.error.key, {
        defaultValue: t('part.form.saveError'),
        ...(updateMut.error.params ?? {}),
      });
    }
    return t('part.form.saveError');
  })();

  const onSubmit = form.handleSubmit((values) => {
    updateMut.mutate(values, {
      onSuccess: (updated) => navigate(`/parts/${updated.id}`),
    });
  });

  return (
    <FormBody
      mode="edit"
      form={form}
      existing={existing}
      partTypeLabel={
        partType
          ? `${partType.code} — ${partType.name}`
          : existing.partTypeId
      }
      title={t('part.form.editTitle')}
      onSubmit={onSubmit}
      submitting={updateMut.isPending}
      submitLabel={t('part.form.submitUpdate')}
      cancelTo={`/parts/${existing.id}`}
      errorMessage={errorMessage}
    />
  );
}

type FormBodyProps =
  | {
      mode: 'create';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form: ReturnType<typeof useForm<any>>;
      existing?: never;
      partTypeLabel?: never;
      title: string;
      onSubmit: (e?: React.BaseSyntheticEvent) => unknown;
      submitting: boolean;
      submitLabel: string;
      cancelTo: string;
      errorMessage: string | null;
    }
  | {
      mode: 'edit';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form: ReturnType<typeof useForm<any>>;
      existing: Part;
      partTypeLabel: string;
      title: string;
      onSubmit: (e?: React.BaseSyntheticEvent) => unknown;
      submitting: boolean;
      submitLabel: string;
      cancelTo: string;
      errorMessage: string | null;
    };

function FormBody(props: FormBodyProps) {
  const { t } = useTranslation();
  const {
    title,
    onSubmit,
    submitting,
    submitLabel,
    cancelTo,
    errorMessage,
    mode,
    form,
  } = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const control = form.control as Control<any>;

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-2">
        <Button
          asChild
          variant="ghost"
          size="icon"
          aria-label={t('common.back')}
        >
          <Link to={cancelTo}>
            <ArrowLeft aria-hidden />
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">{title}</h1>
      </header>

      {mode === 'edit' && (
        <Alert>
          <Info className="h-4 w-4" aria-hidden />
          <AlertDescription>{t('part.form.immutableHint')}</AlertDescription>
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('part.form.saveError')}</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {/* Identity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('part.detail.sectionIdentity')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              {mode === 'create' ? (
                <>
                  <TextField
                    control={control}
                    name="code"
                    label={t('part.fields.code')}
                    hint={t('part.form.codeHint')}
                  />
                  <FormField
                    control={control}
                    name="partTypeId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('part.fields.partTypeId')}
                        </FormLabel>
                        <FormControl>
                          <PartTypePicker
                            value={field.value || null}
                            onChange={(v) => field.onChange(v ?? '')}
                            allowEmpty
                            emptyLabel={t('part.form.partTypePlaceholder')}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              ) : (
                <>
                  <ReadOnlyField
                    label={t('part.fields.code')}
                    value={
                      <span className="font-mono">{props.existing.code}</span>
                    }
                  />
                  <ReadOnlyField
                    label={t('part.fields.partTypeId')}
                    value={
                      <span className="font-mono">
                        {props.partTypeLabel}
                      </span>
                    }
                  />
                </>
              )}
              <TextField
                control={control}
                name="name"
                label={t('part.fields.name')}
              />
              <TextField
                control={control}
                name="unit"
                label={t('part.fields.unit')}
                hint={t('part.form.unitHint')}
              />
              {mode === 'edit' && (
                <ReadOnlyField
                  label={t('part.fields.ownerCompanyId')}
                  value={t(
                    `part.scope.${
                      isGlobalPart(props.existing) ? 'global' : 'company'
                    }`,
                  )}
                />
              )}
              <div className="sm:col-span-2">
                <TextField
                  control={control}
                  name="notes"
                  label={t('part.fields.notes')}
                  textarea
                />
              </div>
            </CardContent>
          </Card>

          {/* Manufacturer */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('part.detail.sectionManufacturer')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <TextField
                control={control}
                name="manufacturer"
                label={t('part.fields.manufacturer')}
              />
              <TextField
                control={control}
                name="manufacturerPartNumber"
                label={t('part.fields.manufacturerPartNumber')}
              />
            </CardContent>
          </Card>

          {/* Stock thresholds */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('part.detail.sectionStock')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <TextField
                control={control}
                name="minStockGlobal"
                label={t('part.fields.minStockGlobal')}
                type="number"
                hint={t('part.form.minStockGlobalHint')}
              />
              <TextField
                control={control}
                name="maxStockGlobal"
                label={t('part.fields.maxStockGlobal')}
                type="number"
                hint={t('part.form.maxStockGlobalHint')}
              />
              {mode === 'edit' && (
                <div className="sm:col-span-2">
                  <CheckboxField
                    control={control}
                    name="isActive"
                    label={t('part.fields.isActive')}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2">
            <Button asChild variant="ghost" disabled={submitting}>
              <Link to={cancelTo}>{t('common.cancel')}</Link>
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <Loader2 className={cn('animate-spin')} aria-hidden />
              ) : (
                <Save aria-hidden />
              )}
              {submitLabel}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export function PartFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();
  const { data: existing, isLoading } = usePart(id);

  if (isEdit && isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isEdit && existing) {
    return <EditBranch existing={existing} />;
  }

  return <CreateBranch defaultPartTypeId={searchParams.get('partTypeId')} />;
}
